// web/src/pages/dashboard/UploadWidget/uploadEngine.js
import axios from "axios";
import { urlMap } from "../../../../utils";

const PART_SIZE = 64 * 1024 * 1024; // 64 MB

export default class UploadEngine {
  constructor({ file, token, onProgress, onComplete, onError, onPart, concurrency = 4, completeExtra = {} }) {
    this.file = file;
    this.token = token;
    this.onProgress = onProgress;
    this.onComplete = onComplete;
    this.onError = onError;
    this.onPart = onPart;
    this.concurrency = concurrency;
    this.completeExtra = completeExtra; // e.g., { contentType, sizeBytes, chapterId, projectId }

    this.controllers = new Map();
    this.partProgress = {};
    this.parts = [];
    this.queue = [];
    this.workerPromises = [];
    this.cancelRequested = false;
    this.paused = false;
  }

  async start() {
    try {
      // INIT
      const auth = { headers: { Authorization: `Bearer ${this.token}` } };
      const initRes = await axios.post(
        urlMap.initMultiPartUpload,
        { filename: this.file.name, filetype: this.file.type },
        auth
      );
      this.uploadId = initRes.data.uploadId;
      this.uploadKey = initRes.data.key;

      // Build queue
      const partCount = Math.ceil(this.file.size / PART_SIZE);
      this.parts = new Array(partCount);
      this.queue = Array.from({ length: partCount }, (_, i) => i + 1);

      await this.kickWorkers(auth);

      if (this.cancelRequested || this.paused) return;

      const finalParts = this.parts.filter(Boolean).sort((a, b) => a.PartNumber - b.PartNumber);
      const { data } = await axios.post(
        urlMap.completeMultiPartUpload,
        { key: this.uploadKey, uploadId: this.uploadId, parts: finalParts, ...this.completeExtra },
        auth
      );

      this.onComplete?.(data);
    } catch (e) {
      if (!this.cancelRequested) this.onError?.(e);
    }
  }

  async kickWorkers(auth) {
    const worker = async () => {
      while (this.queue.length) {
        if (this.paused) {
          await new Promise((resolve) => (this.resumeResolve = resolve));
        }
        if (this.cancelRequested) return;

        const partNumber = this.queue.shift();
        const start = (partNumber - 1) * PART_SIZE;
        const end = Math.min(start + PART_SIZE, this.file.size);
        const blob = this.file.slice(start, end);

        const { data: { url } } = await axios.post(
          urlMap.uploadMultiPartUrl,
          { key: this.uploadKey, uploadId: this.uploadId, partNumber },
          auth
        );

        const controller = new AbortController();
        this.controllers.set(partNumber, controller);

        try {
          const resp = await axios.put(url, blob, {
            headers: { "Content-Type": "application/octet-stream" },
            signal: controller.signal,
            onUploadProgress: ({ loaded }) => {
              this.partProgress[partNumber] = loaded;
              const totalLoaded = Object.values(this.partProgress).reduce((a, b) => a + b, 0);
              const pct = Math.floor((totalLoaded / this.file.size) * 100);
              this.onProgress?.(pct);
            },
          });

          const etag = (resp.headers.etag || resp.headers.ETag || "").replaceAll('"', "");
          this.parts[partNumber - 1] = { ETag: etag, PartNumber: partNumber };
          this.onPart?.(partNumber);
        } catch (err) {
          if (!this.cancelRequested) this.queue.unshift(partNumber); // retry later
        } finally {
          this.controllers.delete(partNumber);
        }
      }
    };

    this.workerPromises = Array.from({ length: this.concurrency }, () => worker());
    await Promise.all(this.workerPromises);
  }

  pause() {
    this.paused = true;
    for (const c of this.controllers.values()) {
      try { c.abort(); } catch {}
    }
  }

  resume() {
    this.paused = false;
    if (this.resumeResolve) {
      this.resumeResolve();
      this.resumeResolve = null;
    }
  }

  cancel() {
    this.cancelRequested = true;
    for (const c of this.controllers.values()) {
      try { c.abort(); } catch {}
    }
  }
}
